import { InteractionType } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router';

import { handleSigsRequest } from './api/sigsClientApi';
import { AuthErrorMessage } from './app';
import {
  getAuthReturnPath,
  parseAuthRedirectState,
} from './auth/authRedirectState';
import { getMainPca } from './auth/mainPca';
import { getMsalInteractionType } from './auth/msalInteractionType';
import { ResponsiveBreakpointProvider } from './components/responsive';
import { AppProvider } from './context/AppContext';
import { createQueryClient } from './query/queryClient';
import { QueryDevtools } from './query/QueryDevtools';
import { router } from './router';
import { CallerProvider } from './store/callerStore';
import { createTelemetryLogger } from './telemetry';
import './index.css';

// One TanStack Query client for the whole app — the shared store for
// WorkflowSvc/Datalake server state (caching, dedup, polling, invalidation).
const queryClient = createQueryClient();

const msalInteractionType = getMsalInteractionType(window.location);

if (
  msalInteractionType === InteractionType.Popup ||
  msalInteractionType === InteractionType.Silent
) {
  // Popup and hidden-iframe callbacks must send their response back to the
  // window that started authentication. Rendering the app here leaves that
  // window waiting until MSAL's bridge timeout expires.
  import('@azure/msal-browser/redirect-bridge')
    .then(({ broadcastResponseToMainFrame }) => broadcastResponseToMainFrame())
    .catch((err: unknown) => {
      if (msalInteractionType === InteractionType.Popup) {
        renderApp().catch((renderError: unknown) => {
          console.error('Failed to render app', renderError);
        });
        return;
      }
      console.error('Failed to complete silent authentication', err);
    });
} else {
  // Either a full-page redirect callback (interactionType === 'redirect') or a
  // normal navigation. In both cases render the app once MSAL has finished its
  // startup handshake — see renderApp() for why this must be awaited.
  renderApp().catch((err: unknown) => {
    console.error('Failed to render app', err);
  });
}

async function renderApp() {
  // Intercept /api/sigs/* fetch calls and handle them in-browser
  // using IndexedDB for storage and Substrate calls via the WorkflowSvc proxy.
  {
    const originalFetch = window.fetch.bind(window);
    window.fetch = (input, init) => {
      const url =
        typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.href
            : input.url;
      if (url.startsWith('/api/sigs')) {
        return handleSigsRequest(new Request(url, init));
      }
      return originalFetch(input, init);
    };
  }

  // Shared MSAL instance for the app's sign-in tenant. DataLake uses its own
  // corporate-tenant instance (getDatalakePca) with a separate cache.
  const msalInstance = getMainPca();

  // msal-browser v3+ requires initialize() before any other API. Without
  // this, acquireTokenSilent calls fired by children at mount can queue
  // behind the still-running startup and hang forever — the "page renders
  // logged-in but workspaces stay loading with no network call" bug.
  await msalInstance.initialize();

  // Process the callback in place. AI Studio restores its return path from
  // user state below instead of relying on MSAL's session-scoped origin cache.
  let redirectResult: Awaited<
    ReturnType<typeof msalInstance.handleRedirectPromise>
  >;
  try {
    redirectResult = await msalInstance.handleRedirectPromise({
      navigateToLoginRequestUrl: false,
    });
  } catch (error: unknown) {
    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const cleanPath = getAuthReturnPath(window.location);
    if (cleanPath !== currentPath) {
      window.history.replaceState(window.history.state, '', cleanPath);
    }
    console.error('Sign-in redirect failed', error);
    const rootElement = document.getElementById('root');
    if (!rootElement) throw error;
    createRoot(rootElement).render(<AuthErrorMessage error={error} />);
    return;
  }

  const returnTo = parseAuthRedirectState(
    redirectResult?.state,
    window.location.origin,
  );
  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const targetPath = returnTo ?? getAuthReturnPath(window.location);
  if (targetPath !== currentPath) {
    // A callback left behind by a previous session must be removed before
    // mounting the router or starting another interactive sign-in.
    window.location.replace(targetPath);
    return;
  }

  // Pin the signed-in account so useAuth.getToken() reads it via
  // getActiveAccount() instead of falling through to getAllAccounts()[0],
  // which is non-deterministic when multiple accounts are cached.
  const active =
    redirectResult?.account ??
    msalInstance.getActiveAccount() ??
    msalInstance.getAllAccounts()[0] ??
    null;
  if (active) {
    msalInstance.setActiveAccount(active);
  }

  // Create the frontend telemetry logger (Teams Standalone Logger → Aria →
  // Geneva). Must run after MSAL is initialized so the logger can attach
  // user/tenant identity. Fail-safe: returns null when no ingestion key is
  // configured and never throws. The logger is handed to <AppProvider> as the
  // `biLogger` (TMP-style), which owns it, derives the scenarioFactory, and
  // fires the one-time app_init boot scenario.
  const biLogger = createTelemetryLogger(msalInstance);

  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('Root element not found');
  }

  createRoot(rootElement).render(
    <StrictMode>
      <ResponsiveBreakpointProvider>
        <MsalProvider instance={msalInstance}>
          <QueryClientProvider client={queryClient}>
            <AppProvider biLogger={biLogger}>
              <CallerProvider>
                <RouterProvider router={router} />
              </CallerProvider>
              <QueryDevtools />
            </AppProvider>
          </QueryClientProvider>
        </MsalProvider>
      </ResponsiveBreakpointProvider>
    </StrictMode>,
  );
}
